import { useEffect, useRef, useState, useCallback } from "react";
import { Plus, ArrowDown, Layers, Globe, Sparkles, Puzzle } from "lucide-react";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import minzoLogo from "@/assets/minzo-logo.png";

/* ───────────────── Data ───────────────── */

const whyItems = [
  {
    title: "One platform for material and systems decision-making",
    desc: "Designers often juggle multiple vendors and systems — MINZO brings them into a unified framework with clear logic, not overwhelm.",
    icon: Layers,
  },
  {
    title: "Expanded access beyond local suppliers",
    desc: "Local markets can be expensive and limited in choice — MINZO provides curated material and system options with global perspectives and vetted quality.",
    icon: Globe,
  },
  {
    title: "Reliable material quality and aesthetic control",
    desc: "Finding a material that looks good on screen and works well in reality is tough — MINZO supports designers in identifying real-world materials that meet both aesthetic and performance needs.",
    icon: Sparkles,
  },
  {
    title: "Consistent design language across materials and systems",
    desc: "Many suppliers focus on products, not design coherence — MINZO focuses on how materials and systems integrate with design intent, not just function.",
    icon: Puzzle,
  },
];

const howSteps = [
  {
    num: "01",
    title: "Share your vision",
    text: "What you're trying to achieve — The feeling, references, and constraints.",
  },
  {
    num: "02",
    title: "We align everything",
    text: "We translate that into a coherent material + system proposal — aligned, not fragmented.",
  },
  {
    num: "03",
    title: "Ready to build",
    text: "You get sourcing that actually holds your design intent — ready to build.",
  },
];

/* ───────────────── Sub-components ───────────────── */

function WhyContent({ isDark }: { isDark: boolean }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  }, []);

  const textMain = isDark ? "text-white/65" : "text-foreground/65";
  const textSub = isDark ? "text-white/45" : "text-foreground/45";
  const iconActive = isDark ? "text-white/70" : "text-foreground/70";
  const iconInactive = isDark ? "text-white/35" : "text-foreground/35";
  const borderDefault = isDark ? "border-white/8" : "border-foreground/8";
  const borderHover = isDark ? "hover:border-white/25" : "hover:border-foreground/25";
  const plusActive = isDark ? "text-white/50" : "text-foreground/50";
  const plusInactive = isDark ? "text-white/25" : "text-foreground/25";

  return (
    <ul className="space-y-5">
      {whyItems.map((item, i) => (
        <li
          key={item.title}
          className={`pl-5 border-l-2 ${borderDefault} cursor-pointer ${borderHover} transition-colors duration-300`}
          onMouseEnter={() => setOpenIndex(i)}
          onClick={() => toggle(i)}
        >
          <div className="flex items-start gap-3">
            <item.icon
              size={16}
              strokeWidth={1.5}
              className={`shrink-0 mt-1 transition-colors duration-300 ${
                openIndex === i ? iconActive : iconInactive
              }`}
            />
            <p className={`font-sans text-[17px] md:text-[19px] font-medium leading-[1.7] ${textMain} tracking-[0.01em]`}>
              {item.title}
            </p>
            <Plus
              size={14}
              strokeWidth={1.5}
              className={`shrink-0 mt-1 transition-all duration-300 ${
                openIndex === i ? `rotate-45 ${plusActive}` : `rotate-0 ${plusInactive}`
              }`}
            />
          </div>
          <div
            className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              openIndex === i ? "max-h-40 opacity-100 mt-1" : "max-h-0 opacity-0"
            }`}
          >
            <p className={`font-sans text-[15px] md:text-[16px] font-normal leading-[1.8] ${textSub} tracking-[0.02em]`}>
              {item.desc}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function HowWorkflow({ isDark }: { isDark: boolean }) {
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState<number | null>(null);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const textTitle = isDark ? "text-white/75" : "text-foreground/75";
  const textTitleActive = isDark ? "text-white/90" : "text-foreground/90";
  const textBody = isDark ? "text-white/45" : "text-foreground/45";
  const textBodyActive = isDark ? "text-white/60" : "text-foreground/60";
  const numActive = isDark ? "text-white/80" : "text-foreground/80";
  const numInactive = isDark ? "text-white/50" : "text-foreground/50";
  const borderInactive = isDark ? "border-white/15" : "border-foreground/15";
  const borderActive = isDark ? "border-white/40" : "border-foreground/40";
  const bgActive = isDark ? "bg-white/5" : "bg-foreground/5";
  const lineActive = isDark ? "bg-white/25" : "bg-foreground/25";
  const lineInactive = isDark ? "bg-white/10" : "bg-foreground/10";
  const arrowActive = isDark ? "text-white/40" : "text-foreground/40";
  const arrowInactive = isDark ? "text-white/20" : "text-foreground/20";

  return (
    <div className="relative">
      {howSteps.map((step, i) => {
        const isActive = activeStep === i;
        return (
          <div
            key={step.num}
            className="relative flex gap-5 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] select-none"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(12px)",
              transitionDelay: `${i * 150}ms`,
            }}
            onMouseEnter={() => setActiveStep(i)}
            onMouseLeave={() => setActiveStep(null)}
          >
            <div className="flex flex-col items-center shrink-0 w-8">
              <div
                className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all duration-300 ${
                  isActive
                    ? `${borderActive} ${bgActive} scale-110`
                    : `${borderInactive} bg-transparent scale-100`
                }`}
              >
                <span
                  className={`text-[10px] font-medium tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? numActive : numInactive
                  }`}
                >
                  {step.num}
                </span>
              </div>
              {i < howSteps.length - 1 && (
                <div
                  className="flex flex-col items-center flex-1 py-1 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{
                    opacity: visible ? 1 : 0,
                    transitionDelay: `${i * 150 + 200}ms`,
                  }}
                >
                  <div
                    className={`w-px flex-1 transition-colors duration-300 ${
                      isActive ? lineActive : lineInactive
                    }`}
                  />
                  <ArrowDown
                    size={10}
                    strokeWidth={1.5}
                    className={`my-0.5 transition-colors duration-300 ${
                      isActive ? arrowActive : arrowInactive
                    }`}
                  />
                </div>
              )}
            </div>

            <div
              className={`pt-1 transition-all duration-300 ${
                i < howSteps.length - 1 ? "pb-8" : "pb-0"
              } ${isActive ? "translate-x-1" : "translate-x-0"}`}
            >
              <h4
                className={`font-sans text-[15px] md:text-[17px] font-medium mb-1 tracking-[0.01em] transition-colors duration-300 ${
                  isActive ? textTitleActive : textTitle
                }`}
              >
                {step.title}
              </h4>
              <p
                className={`font-sans text-[13px] md:text-[14px] font-normal leading-[1.9] tracking-[0.02em] transition-colors duration-300 ${
                  isActive ? textBodyActive : textBody
                }`}
              >
                {step.text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────── Color interpolation ───────────────── */

interface HSL {
  h: number;
  s: number;
  l: number;
}

const colorStops: { light: HSL[]; dark: HSL[] } = {
  light: [
    { h: 220, s: 8, l: 12 },   // What — dark
    { h: 218, s: 4, l: 95 },   // Why — light
    { h: 216, s: 4, l: 91 },   // How — slightly deeper
  ],
  dark: [
    { h: 220, s: 6, l: 8 },
    { h: 218, s: 3, l: 11 },
    { h: 216, s: 3, l: 10 },
  ],
};

function lerpHSL(a: HSL, b: HSL, t: number): string {
  const h = a.h + (b.h - a.h) * t;
  const s = a.s + (b.s - a.s) * t;
  const l = a.l + (b.l - a.l) * t;
  return `hsl(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%)`;
}

function getScrollColor(progress: number, isDarkMode: boolean): string {
  const stops = isDarkMode ? colorStops.dark : colorStops.light;
  const p = Math.max(0, Math.min(1, progress));
  // Stay dark through What (0–0.30), long gradual transition 0.30–0.65, then subtle shift
  if (p <= 0.30) return lerpHSL(stops[0], stops[0], 0);
  if (p <= 0.65) {
    const t = (p - 0.30) / 0.35;
    // Ease-in-out for smoother perceptual transition
    const eased = t * t * (3 - 2 * t);
    return lerpHSL(stops[0], stops[1], eased);
  }
  const t = (p - 0.65) / 0.35;
  return lerpHSL(stops[1], stops[2], t);
}

function getTextureOpacity(progress: number): number {
  if (progress < 0.30) return 0.3;
  if (progress < 0.65) {
    const t = (progress - 0.30) / 0.35;
    return 0.3 + t * 0.7;
  }
  return 1;
}

function isBgDark(progress: number): boolean {
  return progress < 0.48;
}

/* ───────────────── Linen texture ───────────────── */

const linenTexture =
  `radial-gradient(circle at 1px 1px, hsla(40, 20%, 60%, 0.06) 0.5px, transparent 0.5px),
   linear-gradient(90deg, hsla(40, 15%, 70%, 0.03) 1px, transparent 1px),
   linear-gradient(0deg, hsla(40, 15%, 70%, 0.03) 1px, transparent 1px)`;
const linenSize = "4px 4px, 20px 20px, 20px 20px";

/* ───────────────── Main Component ───────────────── */

export function StickyAboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const textureRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const whyRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  const [scrollDark, setScrollDark] = useState(true);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const section = sectionRef.current;
      const bg = bgRef.current;
      const tex = textureRef.current;
      const logo = logoRef.current;
      if (!section || !bg || !tex) return;

      const rect = section.getBoundingClientRect();
      const sectionH = section.offsetHeight;
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / (sectionH - window.innerHeight)));

      const isDarkMode = document.documentElement.classList.contains("dark");
      bg.style.backgroundColor = getScrollColor(progress, isDarkMode);
      tex.style.opacity = String(getTextureOpacity(progress));

      const dark = isBgDark(progress);
      setScrollDark(dark);

      // Parallax: logo moves at 40% scroll speed, also fades out
      if (logo) {
        const logoParallax = scrolled * 0.4;
        const logoOpacity = Math.max(0, 1 - scrolled / (window.innerHeight * 0.8));
        const logoScale = Math.max(0.9, 1 - scrolled / (window.innerHeight * 4));
        logo.style.transform = `translateY(${logoParallax}px) scale(${logoScale})`;
        logo.style.opacity = String(logoOpacity);
      }

      // Subtle parallax for Why and How sections (content moves slightly slower than scroll)
      const applyParallax = (el: HTMLDivElement | null, speed: number) => {
        if (!el) return;
        const elRect = el.getBoundingClientRect();
        const elCenter = elRect.top + elRect.height / 2;
        const viewCenter = window.innerHeight / 2;
        const offset = (elCenter - viewCenter) * speed;
        el.style.transform = `translateY(${offset}px)`;
      };
      applyParallax(whyRef.current, -0.04);
      applyParallax(howRef.current, -0.06);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative isolate">
      {/* Background layer */}
      <div
        ref={bgRef}
        className="absolute inset-0"
        style={{ backgroundColor: "hsl(220, 15%, 12%)" }}
      />
      <div
        ref={textureRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: linenTexture,
          backgroundSize: linenSize,
          opacity: 0,
        }}
      />

      {/* Content sections */}
      <div className="relative z-10">
        {/* Logo with parallax — pushed down so it doesn't overlap hero logo */}
        <div
          ref={logoRef}
          className="flex justify-center pt-[30vh] pb-10 md:pb-14 will-change-[transform,opacity]"
        >
          <ScrollFadeIn>
            <img
              src={minzoLogo}
              alt="MINZO"
              className={`h-24 md:h-36 transition-[filter] duration-700 ${
                scrollDark ? "brightness-0 invert" : "brightness-0 dark:invert"
              }`}
            />
          </ScrollFadeIn>
        </div>

        {/* ── WHAT ── (was Why position — dark bg) */}
        <div className="py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <ScrollFadeIn>
                <h2
                  className={`text-[20px] md:text-[24px] font-light tracking-[0.12em] uppercase mb-8 transition-colors duration-500 ${
                    scrollDark ? "text-white/80" : "text-foreground/80"
                  }`}
                >
                  What
                </h2>
              </ScrollFadeIn>
              <ScrollFadeIn delay={150}>
                <p className={`font-sans text-[22px] md:text-[28px] font-light leading-[1.5] tracking-[0.005em] transition-colors duration-500 ${
                  scrollDark ? "text-white/80" : "text-foreground/80"
                }`}>
                  Source materials, furniture, and systems —
                  <br />
                  as one aligned whole.
                </p>
              </ScrollFadeIn>
              <ScrollFadeIn delay={350}>
                <p className={`font-sans text-[14px] md:text-[16px] font-normal leading-[1.9] tracking-[0.03em] mt-12 transition-colors duration-500 ${
                  scrollDark ? "text-white/45" : "text-foreground/45"
                }`}>
                  No mismatches.
                  <br />
                  No "close enough."
                  <br />
                  No fragmentation.
                </p>
              </ScrollFadeIn>
              <ScrollFadeIn delay={550}>
                <p className={`font-sans text-[17px] md:text-[20px] font-light italic leading-[1.6] tracking-[0.02em] mt-14 transition-colors duration-500 ${
                  scrollDark ? "text-white/65" : "text-foreground/65"
                }`}>
                  So what gets built
                  <br />
                  matches what was designed.
                </p>
              </ScrollFadeIn>
            </div>
          </div>
        </div>

        {/* ── WHY ── (was What position — bright bg) */}
        <div ref={whyRef} className="py-16 md:py-24 will-change-transform">
          <div className="container mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <ScrollFadeIn>
                <h2 className="text-[20px] md:text-[24px] font-light tracking-[0.12em] uppercase text-foreground/80 mb-8">
                  Why
                </h2>
              </ScrollFadeIn>
              <ScrollFadeIn delay={150}>
                <WhyContent isDark={false} />
              </ScrollFadeIn>
            </div>
          </div>
        </div>

        {/* ── HOW ── */}
        <div ref={howRef} className="py-16 md:py-24 will-change-transform">
          <div className="container mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <ScrollFadeIn>
                <h2 className="text-[20px] md:text-[24px] font-light tracking-[0.12em] uppercase text-foreground/80 mb-8">
                  How
                </h2>
              </ScrollFadeIn>
              <ScrollFadeIn delay={150}>
                <HowWorkflow isDark={false} />
              </ScrollFadeIn>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
